import { Dispatch } from '@reduxjs/toolkit';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { getPeriod } from 'dashboard/lib/utils/charting';
import { formatDateRangeEnd, formatDateRangeStart } from 'dashboard/lib/utils/formatters';
import { Environment } from 'dashboard/models/environment';
import { ReportsMetricsSummary } from 'dashboard/models/metrics-reports';
import {
  setReportMetricsGranularities,
  setReportsMetricsVolumeOverTime,
  setReportsMetricsVolumeSummary,
} from 'dashboard/redux/actions/metrics-reports';
import {
  ReportsGranularities,
  ReportsMetricsFilterParams,
} from 'dashboard/redux/reducers/metrics-reports';
import { ApiEndpoint } from 'lib/interfaces/api';
import { Iso8601DateString } from 'lib/types';

export interface VolumeSummaryResponse {
  meta: ReportsMetricsSummary[];
  updated_at: Iso8601DateString;
}

export function loadReportsVolumeSummary({
  currentEnvironment,
  filters,
}: {
  currentEnvironment: Environment;
  filters: ReportsMetricsFilterParams;
}) {
  return async (dispatch: Dispatch) => {
    const { createdAtStart, createdAtEnd, templateIds, tagIds } = filters;

    const volumeSummary = await SessionClientDashboard.getEndpoint<VolumeSummaryResponse>(
      ApiEndpoint.MetricsReportsSummary,
      {
        filter: {
          'environment-id': currentEnvironment.id,
          'tag-ids': tagIds,
          'template-ids': templateIds,
          'created-at-start': formatDateRangeStart(createdAtStart),
          'created-at-end': formatDateRangeEnd(createdAtEnd),
        },
      },
    );

    dispatch(
      setReportsMetricsVolumeSummary(
        volumeSummary.meta,
        new Date().toISOString(),
        volumeSummary.updated_at,
      ),
    );
  };
}

export function setGranularities({ granularities }: { granularities: ReportsGranularities }) {
  return async (dispatch: Dispatch) => {
    dispatch(setReportMetricsGranularities(granularities));
  };
}

export function loadReportsOverTime({
  currentEnvironment,
  filters,
  granularities,
}: {
  currentEnvironment: Environment;
  filters: ReportsMetricsFilterParams;
  granularities: ReportsGranularities;
}) {
  return async (dispatch: Dispatch) => {
    const { createdAtStart, createdAtEnd, templateIds, tagIds } = filters;

    const reportsOverTime = (
      await SessionClientDashboard.getResourceMetaTyped<ApiEndpoint.MetricsReportsOverTime>(
        ApiEndpoint.MetricsReportsOverTime,
        {
          filter: {
            'environment-id': currentEnvironment.id,
            'template-ids': templateIds,
            'tag-ids': tagIds,
            'created-at-start': formatDateRangeStart(createdAtStart),
            'created-at-end': formatDateRangeEnd(createdAtEnd),
            period: getPeriod(createdAtStart, createdAtEnd),
            granularities: JSON.stringify(granularities),
          },
        },
      )
    ).meta;

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    dispatch(setReportsMetricsVolumeOverTime(reportsOverTime, new Date().toISOString()));
  };
}
